import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Utensils, ChefHat, Crown, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { usePlans, getFeatureDisplayText } from '@/hooks/usePlans';

interface PricingTableProps {
  showToggle?: boolean;
  defaultBilling?: 'monthly' | 'quarterly' | 'annual';
  className?: string;
}

const PricingTable: React.FC<PricingTableProps> = ({ 
  showToggle = true, 
  defaultBilling = 'annual',
  className = ''
}) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'quarterly' | 'annual'>(defaultBilling);
  const { data: plansData, loading, error } = usePlans();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-white text-xl">Loading pricing plans...</div>
      </div>
    );
  }

  if (error || !plansData) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-white text-xl">Failed to load pricing. Please refresh the page.</div>
      </div>
    );
  }

  // Transform API data to match our component structure
  const plans = plansData.plans.map(plan => ({
    id: plan.name,
    name: plan.displayName,
    description: plan.description,
    isCustomPricing: plan.isCustomPricing,
    icon: plan.name === 'yummy' ? Utensils : plan.name === 'delicious' ? ChefHat : Crown,
    iconColor: plan.name === 'yummy' ? 'text-green-400' : plan.name === 'delicious' ? 'text-blue-400' : 'text-purple-400',
    gradient: plan.name === 'yummy' ? 'from-green-400 to-green-600' :
              plan.name === 'delicious' ? 'from-blue-400 to-blue-600' :
              'from-purple-400 to-purple-600',
    pricing: plan.pricing,
    features: getFeatureDisplayText(plan),
    mostPopular: plan.name === 'delicious'
  }));

  const getPrice = (plan: typeof plans[0]) => {
    switch (billingPeriod) {
      case 'quarterly':
        // Use quarterly pricing if available, otherwise calculate 10% discount
        return plan.pricing.quarterly?.monthlyEquivalent || (plan.pricing.monthly.price * 0.9);
      case 'annual':
        // Use annual pricing if available, otherwise calculate 20% discount
        return plan.pricing.annual?.monthlyEquivalent || (plan.pricing.monthly.price * 0.8);
      default:
        return plan.pricing.monthly.price;
    }
  };

  const getSavings = (plan: typeof plans[0]) => {
    const monthlyPrice = plan.pricing.monthly.price;
    switch (billingPeriod) {
      case 'quarterly':
        // 10% savings on quarterly (3 months)
        const quarterlySavings = monthlyPrice * 0.1 * 3;
        return plan.pricing.quarterly?.savings || quarterlySavings;
      case 'annual':
        // 20% savings on annual (12 months)
        const annualSavings = monthlyPrice * 0.2 * 12;
        return plan.pricing.annual?.savings || annualSavings;
      default:
        return 0;
    }
  };

  return (
    <div className={`py-16 ${className}`}>
      {showToggle && (
        <div className="flex justify-center items-center mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-full p-1 flex items-center">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-blue-600'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('quarterly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                billingPeriod === 'quarterly'
                  ? 'bg-white text-blue-600'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Quarterly
              {billingPeriod === 'quarterly' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                >
                  Save 10%
                </motion.div>
              )}
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                billingPeriod === 'annual'
                  ? 'bg-white text-blue-600'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Annual
              {billingPeriod === 'annual' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap"
                >
                  Save 20%
                </motion.div>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
        {plans.map((plan, index) => {
          const price = getPrice(plan);
          const savings = getSavings(plan);
          const basePrice = plan.pricing.monthly.price;
          
          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              className={`relative bg-white/10 backdrop-blur-md rounded-2xl p-8 ${
                plan.mostPopular 
                  ? 'border-2 border-yellow-400/50 transform scale-105' 
                  : 'border border-white/20'
              }`}
            >
              {plan.mostPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center`}>
                  <plan.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold mb-2 text-white">{plan.name}</h3>
                <p className="text-white/70 text-sm mb-4">{plan.description}</p>

                {plan.isCustomPricing ? (
                  <>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-white">Custom</span>
                    </div>
                    <div className="text-sm text-white/60 mb-2">
                      Contact us for pricing
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-2">
                      <span className="text-4xl font-bold text-white">
                        ${price.toFixed(2)}
                      </span>
                      <span className="text-white/60">/month</span>
                    </div>

                    {billingPeriod !== 'monthly' && (
                      <div className="text-sm">
                        <span className="line-through text-white/50">${basePrice.toFixed(2)}/mo</span>
                        <span className="text-green-400 ml-2">
                          Save ${savings.toFixed(0)}/year
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 ${plan.iconColor} flex-shrink-0 mt-0.5`} />
                    <span className="text-white/90 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.isCustomPricing ? (
                <a href="mailto:support@dineai.ai?subject=Gourmet Plan Inquiry&body=I am interested in learning more about the Gourmet plan for my restaurant."
                   className="block">
                  <Button
                    className="w-full py-3 font-semibold rounded-xl transition-all duration-300 bg-white/20 hover:bg-white/30 text-white border border-white/30"
                  >
                    Contact Sales
                  </Button>
                </a>
              ) : (
                <Link to="/restaurant-menu-search"
                      onClick={() => {
                        // Store plan for after signup
                        localStorage.setItem('selected_plan', plan.id);
                        localStorage.setItem('billing_period', billingPeriod);
                      }}
                      className="block">
                  <Button
                    className={`w-full py-3 font-semibold rounded-xl transition-all duration-300 ${
                      plan.mostPopular
                        ? 'bg-dineai-yellow hover:bg-dineai-yellow/90 text-black shadow-lg'
                        : 'bg-white/20 hover:bg-white/30 text-white border border-white/30'
                    }`}
                  >
                    Start 30-Day Free Trial
                  </Button>
                </Link>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingTable;